import { X, Pin, PinOff } from 'lucide-react';
import { SidebarContent } from './SidebarContent';
import { useTranslation } from 'react-i18next';
import { useSidebar } from './SidebarContext';
import { useRef } from 'react';

interface SidebarProps {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ sidebarOpen, toggleSidebar }) => {
  const { i18n } = useTranslation();
  const { isCollapsed, isHovered, isPinned, setIsHovered, setIsPinned } = useSidebar();

  const collapsedWidth = 'w-24';
  const expandedWidth = 'w-56';

  const sidebarWidth =
    isCollapsed && !isHovered && !isPinned
      ? collapsedWidth
      : expandedWidth;

  //to make the state change with
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseLeave = () => {
    if (isPinned) return;

    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 500);
  };

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    setIsHovered(true);
  };

  return (
    <>
      <aside
        className={`
    fixed top-0 h-screen w-[286px] bg-white z-[200] overflow-hidden
    ltr:left-0 rtl:right-0 transition-transform
    ${sidebarOpen ? 'translate-x-0' : 'ltr:-translate-x-full rtl:translate-x-full'}
    ${i18n.language === 'ar' ? 'shadow-lg shadow-left' : 'shadow-lg shadow-right'}
    lg:hidden
  `}
      >
        <div className="p-4 h-full overflow-y-auto overscroll-contain">
          <SidebarContent onLinkClick={toggleSidebar} />
        </div>

        <button
          className="absolute top-4 ltr:right-4 rtl:left-4 text-secondary hover:text-primary"
          onClick={toggleSidebar}
        >
          <X size={28} />
        </button>
      </aside>

      <aside
        className={`
        hidden lg:flex flex-col h-screen bg-white border-r border-slate-200 z-20
        transition-[width] duration-700 ease-in-out overflow-hidden

    ${i18n.language === 'ar' ? 'right-0 shadow-lg shadow-left' : 'left-0 shadow-lg shadow-right'}
    ${sidebarWidth}
  `} onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}

      >

        <div className="p-4 flex flex-col flex-1 overflow-y-auto overscroll-contain">
          {(isHovered || isPinned) && (
            <div
              className={`mb-4 flex ${i18n.language === 'ar' ? 'justify-start' : 'justify-end'
                }`}
            >
              <button
                onClick={() => setIsPinned(!isPinned)}
                className="p-1.5 rounded-md hover:text-yellow-500"
              >
                {isPinned ? <PinOff size={18} /> : <Pin size={18} />}
              </button>
            </div>
          )}

          {(isHovered || isPinned) && (
            <img
              src="/logo.png"
              alt="Logo"
              className="w-20 mx-auto mb-4"
            />
          )}

          <SidebarContent
            isCollapsed={isCollapsed}
            isHovered={isHovered}
            isPinned={isPinned}
          />
        </div>
      </aside>
    </>
  );
};

export default Sidebar;